import React, { useEffect } from 'react';
import { Input, FilterPanel, useToast } from '@aidkitorg/component-library';
import { ApplicantGroupFilterConfig } from '@aidkitorg/types/lib/admin-panel/filters';
import { useLocalizedStrings } from '../../../Localization';
import { useLocalFilterPanel, useSettingsFilterFields } from '../../Filters';
import { ApplicantDataTable } from './ApplicantDataTable';
import { useFieldValueSuggestions } from '../../shared/hooks/useFieldValueSuggestions';

export interface ApplicantGroupFormProps {
  mode: 'create' | 'edit';
  groupId?: string;
  initialValues?: {
    name: string;
    description: string;
    filters: ApplicantGroupFilterConfig;
  };
  onSubmit: (data: {
    name: string;
    description: string;
    filters: ApplicantGroupFilterConfig;
  }) => Promise<void>;
  isSubmitting: boolean;
  name: string;
  description: string;
  showValidation: boolean;
  setShowValidation: (show: boolean) => void;
}

export const ApplicantGroupForm: React.FC<ApplicantGroupFormProps> = ({
  mode,
  groupId,
  initialValues,
  onSubmit,
  isSubmitting,
  name,
  description,
  showValidation,
  setShowValidation
}) => {
  const L = useLocalizedStrings();
  const strings = L.admin_panel.settings.applicant_groups;
  const { toast } = useToast();

  const instanceId = mode === 'create' ? 'applicant-group-create' : `applicant-group-edit-${groupId}`;
  const availableFields = useSettingsFilterFields();
  const { filterPanelProps, filterConfig, setFilterConfig, filterRef } = useLocalFilterPanel(instanceId, {
    availableFields,
    enableFlushOnMount: true
  });
  const { fetchSuggestions } = useFieldValueSuggestions();

  // Set initial filter config when in edit mode on mount
  useEffect(() => {
    if (mode === 'edit' && initialValues?.filters) {
      setFilterConfig(initialValues.filters);
    }
  }, [mode, initialValues?.filters, setFilterConfig]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Flush FilterPanel state to sync with parent state
    if (filterRef.current?.flush) {
      filterRef.current.flush();
    }

    // Wait for next tick to ensure state has been updated
    await new Promise(resolve => setTimeout(resolve, 0));

    const nameValue = name.trim();
    const descriptionValue = description.trim();

    if (!nameValue) {
      setShowValidation(true);
      return;
    }

    // Validate that filters are configured
    if (!filterConfig.enabled ||
        !filterConfig.config ||
        !filterConfig.config.logic ||
        !filterConfig.config.conditions ||
        filterConfig.config.conditions.length === 0) {
      toast({
        title: strings.error,
        description: strings.error_message_filter,
        variant: 'error'
      });
      return;
    }

    try {
      await onSubmit({
        name: nameValue,
        description: descriptionValue,
        filters: filterConfig
      });
    } catch (error) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} applicant group:`, error);
      toast({
        title: strings.error,
        description: mode === 'create' ? strings.error_message_create : strings.error_message_update,
        variant: 'error'
      });
    }
  };

  // Use initialValues for FilterPanel value in edit mode, otherwise use filterConfig
  const filterPanelValue = (mode === 'edit' && initialValues?.filters) ? initialValues.filters : filterConfig;
  console.log("value", filterPanelValue)

  return (
    <div className="w-full space-y-6">
      <form id="applicant-group-form" onSubmit={handleSubmit} className="w-full">
        <FilterPanel
          {...filterPanelProps}
          value={filterPanelValue}
          isReloading={isSubmitting}
          inline={true}
          fetchFieldValues={fetchSuggestions}
        />
      </form>
      <ApplicantDataTable
        filters={filterConfig}
        isLoading={isSubmitting}
      />
    </div>
  );
};

